/**
 * RKL / ExecuteConfirmation — "type the target id" confirm-then-execute dialog.
 *
 * Prevents fat-finger executions by requiring the verifier to retype the
 * target identifier before the destructive call goes out.
 */

import { useState } from 'react';
import type { KillListEntryDto } from '@retail-ops/shared-types';
import { Button, Pill } from '@/components/primitives';

export interface ExecuteConfirmationProps {
  entry: KillListEntryDto;
  onClose: () => void;
  onConfirm: () => void;
}

export function ExecuteConfirmation({ entry, onClose, onConfirm }: ExecuteConfirmationProps): JSX.Element {
  const [typed, setTyped] = useState<string>('');
  const match = typed.trim() === entry.targetId;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-bg)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 30,
      }}
    >
      <div
        style={{
          background: 'var(--surface-card)',
          border: `2px solid var(--pcb-red)`,
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-8)',
          width: 'min(100%, 520px)',
          boxShadow: 'var(--sh-4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', marginBottom: 'var(--sp-5)' }}>
          <span aria-hidden style={{ fontSize: 24 }}>⚠️</span>
          <h3 style={{ margin: 0, color: 'var(--pcb-red)' }}>Execute destructive kill</h3>
          <Pill variant="err">Step 2 of 2</Pill>
        </div>

        <p style={{ fontSize: 13, color: 'var(--ink-800)', lineHeight: 1.5 }}>
          Executing this entry will call <code>/accounts/kill</code> (or the appropriate jXRL endpoint)
          for <strong>{entry.entryType}</strong> targeting <code>{entry.targetId}</code>.
        </p>
        <p style={{ fontSize: 13, color: 'var(--ink-800)', lineHeight: 1.5 }}>
          <strong>Consequences.</strong> The target record will be marked terminated in core systems.
          If RetentionHold is false the source cart will eventually purge at the 7-yr mark.
        </p>

        <label
          htmlFor="kl-confirm-target"
          style={{
            display: 'block',
            fontSize: 11,
            textTransform: 'uppercase',
            color: 'var(--ink-500)',
            marginBottom: 'var(--sp-3)',
            marginTop: 'var(--sp-5)',
          }}
        >
          Type the target id to confirm: <code>{entry.targetId}</code>
        </label>
        <input
          id="kl-confirm-target"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          style={{
            width: '100%',
            padding: 'var(--sp-4)',
            border: `1px solid ${match ? 'var(--pcb-green)' : 'var(--ink-300)'}`,
            borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font-mono)',
            fontSize: 14,
          }}
        />

        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-6)', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={!match}>
            Execute kill
          </Button>
        </div>
      </div>
    </div>
  );
}
